import { Link, type LinkOptions } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { cx } from './cx'

/** Hairline-separated rows. Put it in a Card when it is a standalone block, or bare under a Section. */
export function List({ children, label, className }: { children: ReactNode; label?: string; className?: string }) {
  return (
    <ul aria-label={label} className={cx('divide-y divide-line', className)}>
      {children}
    </ul>
  )
}

type ListItemProps = {
  title: ReactNode
  /** Secondary line — 1–2 values that decide, not a data dump. */
  meta?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  link?: LinkOptions
  onClick?: () => void
  /** Inset the row content so hover backgrounds do not touch the container edge (inside Cards). */
  inset?: boolean
}

export function ListItem({ title, meta, leading, trailing, link, onClick, inset }: ListItemProps) {
  const interactive = Boolean(link || onClick)
  const content = (
    <>
      {leading && <span className="flex shrink-0 items-center">{leading}</span>}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-body text-ink">{title}</span>
        {meta && <span className="truncate text-small text-ink-3">{meta}</span>}
      </span>
      {trailing && <span className="flex shrink-0 items-center gap-2 text-small text-ink-2">{trailing}</span>}
      {interactive && <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-ink-3" />}
    </>
  )
  const cls = cx(
    'flex min-h-14 w-full items-center gap-3 py-2.5 text-left',
    inset && 'px-4 md:px-5',
    interactive && 'transition-colors duration-150 hover:bg-bg-subtle focus-visible:outline-offset-[-2px]',
    interactive && !inset && '-mx-2 w-[calc(100%+1rem)] rounded-control px-2',
  )
  return (
    <li>
      {link ? (
        <Link {...link} className={cls}>
          {content}
        </Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} className={cls}>
          {content}
        </button>
      ) : (
        <div className={cls}>{content}</div>
      )}
    </li>
  )
}
